import { Button, Empty, Icon } from "@dgs/core";
import { useFormikContext } from "formik";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { GuestSelfTravelResource } from "../../../../../shared/types/selfTravel";
import { GuestTravelInquiry } from "../../../../../shared/types/travelInquiry";
import { TravelSectionSettingsResource } from "../../../shared/types";
import { GuestTravelFormValues } from "../../../shared/types/travel";
import {
	BaseTravelSegment,
	TravelDirection,
	TravelSegmentFormValues,
	TravelSegmentIndexResource,
} from "../../../shared/types/travelSegment";
import { TravelTemplateIndexResource } from "../../../shared/types/travelTemplate";
import { useRegistrationForm } from "../../provider/registrationFormProviderContext";
import { RegistrationTravelSectionTitle } from "./RegistrationTravelSectionTitle";
import { Travel } from "./Travel";
import { TravelValidationResult, validateTravels } from "./validateTravels";

interface Props {
	travelSectionSettings: TravelSectionSettingsResource;
}

const List = styled.ul`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(6)};
	list-style: none;
`;

const Container = styled.section`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(6)};
	padding: ${({ theme }) => theme.spacing(6)} 0;
`;

const emptyTravel: GuestTravelFormValues = {
	id: -1,
	arrivals: [],
	departures: [],
	arrivalInquiry: null,
	departureInquiry: null,
	departureSelfTravel: null,
	arrivalSelfTravel: null,
	departureTravelTemplate: null,
	arrivalTravelTemplate: null,
	arrivalRemarks: null,
	departureRemarks: null,
};

const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
`;

const sortByArrivalDateAndTime = (a: BaseTravelSegment, b: BaseTravelSegment) => {
	return `${a.arrivalDate} ${a.arrivalTime}`.localeCompare(`${b.arrivalDate} ${b.arrivalTime}`);
};

export const RegistrationTravelSection: FC<Props> = ({ travelSectionSettings }) => {
	const { t } = useTranslation();
	const { travels, setTravels, registrationForm } = useRegistrationForm();
	const [errors, setErrors] = useState<TravelValidationResult | null>(() => {
		return validateTravels(travelSectionSettings, travels, t);
	});
	const { submitCount } = useFormikContext();
	const maxTravels = travelSectionSettings.maxTravels || Infinity;

	const validateAndSetTravels = useCallback(
		(newTravels: GuestTravelFormValues[]) => {
			setErrors(validateTravels(travelSectionSettings, newTravels, t));
			setTravels(newTravels);
		},
		[setTravels, t, travelSectionSettings],
	);

	const modifyTravelAtIndex = useCallback(
		(atIndex: number, modifyFn: (travel: GuestTravelFormValues) => GuestTravelFormValues) => {
			const newTravels = [...travels];
			newTravels[atIndex] = modifyFn(newTravels[atIndex]);
			validateAndSetTravels(newTravels);
		},
		[travels, validateAndSetTravels],
	);

	const handleDeleteTravel = useCallback(
		(index: number) => {
			const newTravels = [...travels];
			newTravels.splice(index, 1);
			validateAndSetTravels(newTravels);
		},
		[travels, validateAndSetTravels],
	);

	const handleCreateTravel = useCallback(() => {
		validateAndSetTravels([...travels, emptyTravel]);
	}, [travels, validateAndSetTravels]);

	const handleRemoveTravelSegment = useCallback(
		(
			direction: TravelDirection,
			travelIndex: number,
			segment: TravelSegmentIndexResource | TravelSegmentFormValues,
		) => {
			modifyTravelAtIndex(travelIndex, (travel) => {
				switch (direction) {
					case "arrival":
						return {
							...travel,
							arrivals: travel.arrivals.filter(({ id }) => id !== segment.id),
						};
					case "departure":
						return {
							...travel,
							departures: travel.departures.filter(({ id }) => id !== segment.id),
						};
				}
			});
		},
		[modifyTravelAtIndex],
	);

	const handleAddTravelSegments = useCallback(
		(
			direction: TravelDirection,
			travelIndex: number,
			selection: (TravelSegmentIndexResource | TravelSegmentFormValues)[],
		) => {
			modifyTravelAtIndex(travelIndex, (travel) => {
				switch (direction) {
					case "arrival":
						return {
							...travel,
							arrivalTravelTemplate: null,
							arrivalSelfTravel: null,
							arrivalInquiry: null,
							arrivals: [...travel.arrivals, ...selection].sort(sortByArrivalDateAndTime),
						};
					case "departure":
						return {
							...travel,
							departureTravelTemplate: null,
							departureSelfTravel: null,
							departureInquiry: null,
							departures: [...travel.departures, ...selection].sort(sortByArrivalDateAndTime),
						};
				}
			});
		},
		[modifyTravelAtIndex],
	);

	const handleChangeTravelInquiry = useCallback(
		(direction: TravelDirection, travelIndex: number, inquiry: GuestTravelInquiry) => {
			modifyTravelAtIndex(travelIndex, (travel) => {
				switch (direction) {
					case "arrival":
						return {
							...travel,
							arrivals: [],
							arrivalTravelTemplate: null,
							arrivalSelfTravel: null,
							arrivalInquiry: inquiry,
						};
					case "departure":
						return {
							...travel,
							departures: [],
							departureTravelTemplate: null,
							departureSelfTravel: null,
							departureInquiry: inquiry,
						};
				}
			});
		},
		[modifyTravelAtIndex],
	);

	const handleRemoveTravelInquiry = useCallback(
		(direction: TravelDirection, travelIndex: number) => {
			modifyTravelAtIndex(travelIndex, (travel) => {
				switch (direction) {
					case "arrival":
						return { ...travel, arrivalInquiry: null };
					case "departure":
						return { ...travel, departureInquiry: null };
				}
			});
		},
		[modifyTravelAtIndex],
	);

	const handleChangeSelfTravel = useCallback(
		(direction: TravelDirection, travelIndex: number, selfTravel: GuestSelfTravelResource) => {
			modifyTravelAtIndex(travelIndex, (travel) => {
				switch (direction) {
					case "arrival":
						return {
							...travel,
							arrivals: [],
							arrivalInquiry: null,
							arrivalTravelTemplate: null,
							arrivalSelfTravel: selfTravel,
						};
					case "departure":
						return {
							...travel,
							departures: [],
							departureInquiry: null,
							departureTravelTemplate: null,
							departureSelfTravel: selfTravel,
						};
				}
			});
		},
		[modifyTravelAtIndex],
	);

	const handleRemoveSelfTravel = useCallback(
		(direction: TravelDirection, travelIndex: number) => {
			modifyTravelAtIndex(travelIndex, (travel) => {
				switch (direction) {
					case "arrival":
						return { ...travel, arrivalSelfTravel: null };
					case "departure":
						return { ...travel, departureSelfTravel: null };
				}
			});
		},
		[modifyTravelAtIndex],
	);

	const onSelectTravelTemplate = useCallback(
		(direction: TravelDirection, travelIndex: number, travelTemplate: TravelTemplateIndexResource) => {
			modifyTravelAtIndex(travelIndex, (travel) => {
				switch (direction) {
					case "arrival":
						return {
							...travel,
							arrivalInquiry: null,
							arrivalSelfTravel: null,
							arrivals: [],
							arrivalTravelTemplate: travelTemplate,
						};
					case "departure":
						return {
							...travel,
							departureInquiry: null,
							departureSelfTravel: null,
							departures: [],
							departureTravelTemplate: travelTemplate,
						};
				}
			});
		},
		[modifyTravelAtIndex],
	);

	return (
		<Container>
			<SectionHeader>
				<RegistrationTravelSectionTitle
					maxTravels={travelSectionSettings.maxTravels}
					travelsCount={travels.length}
					error={submitCount > 0 && errors?.count}
				/>
				<Button
					type="button"
					color="primary"
					onClick={handleCreateTravel}
					prefix={<Icon $icon="plus" />}
					disabled={travels.length >= maxTravels}
					title={
						travels.length >= maxTravels
							? t("You can only assign a maximum of {{count}} travels.", { count: maxTravels })
							: undefined
					}
				>
					{t("Add arrival and departure")}
				</Button>
			</SectionHeader>
			{travels.length === 0 && <Empty>{t("No travel available.")}</Empty>}
			{travels.length !== 0 && (
				<List>
					{travels.map((travel, index) => (
						<li key={index}>
							<Travel
								errors={submitCount > 0 && errors?.travels?.[index]}
								registrationFormId={registrationForm.id}
								heading={t("{{number}}. Travel", { number: index + 1 })}
								travel={travel}
								onDelete={() => handleDeleteTravel(index)}
								onRemoveTravelSegment={(direction, segment) => handleRemoveTravelSegment(direction, index, segment)}
								onAddTravelSegments={(direction, selection) => handleAddTravelSegments(direction, index, selection)}
								onChangeTravelInquiry={(direction, inquiry) => handleChangeTravelInquiry(direction, index, inquiry)}
								onRemoveTravelInquiry={(direction) => handleRemoveTravelInquiry(direction, index)}
								onChangeSelfTravel={(direction, selfTravel) => handleChangeSelfTravel(direction, index, selfTravel)}
								onRemoveSelfTravel={(direction) => handleRemoveSelfTravel(direction, index)}
								onSelectTravelTemplate={(direction, travelTemplate) =>
									onSelectTravelTemplate(direction, index, travelTemplate)
								}
								settings={travelSectionSettings}
							/>
						</li>
					))}
				</List>
			)}
		</Container>
	);
};
